'use client';

import * as React from 'react';
import { LuTriangleAlert, LuRefreshCw, LuX } from 'react-icons/lu';

import { Box, HStack, VStack } from '../index';

import { Alert } from './alert';
import { Button } from './button';
import { IconButton } from './icon-button';

export interface ErrorAlertProps {
  /**
   * Error object or message to display
   */
  error: Error | string | null | undefined;

  /**
   * Title of the alert
   * @default 'Error'
   */
  title?: string;

  /**
   * Custom error message (overrides error.message)
   */
  message?: string;

  /**
   * Callback when retry button is clicked
   */
  onRetry?: () => void;

  /**
   * Callback when dismiss button is clicked
   */
  onDismiss?: () => void;

  /**
   * Show retry button
   * @default true
   */
  showRetry?: boolean;

  /**
   * Show dismiss button
   * @default true
   */
  showDismiss?: boolean;

  /**
   * Retry button label
   * @default 'Retry'
   */
  retryLabel?: string;

  /**
   * Alert color palette
   * @default 'red'
   */
  colorPalette?: 'red' | 'orange' | 'yellow' | 'blue' | 'gray';

  /**
   * Show error details (error.stack or full error object)
   * @default false
   */
  showDetails?: boolean;

  /**
   * Custom className for styling
   */
  className?: string;

  /**
   * Loading state for retry button
   */
  isRetrying?: boolean;
}

/**
 * ErrorAlert - Standardized error display component
 *
 * Features:
 * - Displays error messages from Error objects or strings
 * - Optional retry functionality with loading state
 * - Dismissible with callback
 * - Expandable error details (stack trace)
 * - Consistent styling with Chakra UI Alert
 * - Accessible with ARIA labels
 *
 * @example
 * ```tsx
 * const { error, refetch, isLoading } = useQuery(...);
 *
 * {error && (
 *   <ErrorAlert
 *     error={error}
 *     onRetry={refetch}
 *     isRetrying={isLoading}
 *     onDismiss={() => queryClient.resetQueries()}
 *   />
 * )}
 * ```
 *
 * @example
 * ```tsx
 * // Custom messages and styling
 * <ErrorAlert
 *   error={error}
 *   title="Veri Yüklenemedi"
 *   message="Müşteri bilgileri alınamadı. Lütfen tekrar deneyin."
 *   retryLabel="Tekrar Dene"
 *   colorPalette="orange"
 *   showDetails
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Simple error display (no retry)
 * <ErrorAlert
 *   error="Invalid input data"
 *   showRetry={false}
 *   colorPalette="yellow"
 * />
 * ```
 */
export function ErrorAlert({
  error,
  title = 'Error',
  message,
  onRetry,
  onDismiss,
  showRetry = true,
  showDismiss = true,
  retryLabel = 'Retry',
  colorPalette = 'red',
  showDetails = false,
  className,
  isRetrying = false,
}: ErrorAlertProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Extract error message - must be before early return for React Hooks rules
  const errorMessage = React.useMemo(() => {
    if (!error) return '';
    if (message) return message;
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message;
    return 'An unexpected error occurred';
  }, [error, message]);

  // Extract error details - must be before early return for React Hooks rules
  const errorDetails = React.useMemo(() => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error instanceof Error) {
      return error.stack || JSON.stringify(error, null, 2);
    }
    return JSON.stringify(error, null, 2);
  }, [error]);

  // Don't render if no error - must be after all hooks
  if (!error) {
    return null;
  }

  return (
    <Alert.Root colorPalette={colorPalette} className={className}>
      <Alert.Indicator>
        <LuTriangleAlert />
      </Alert.Indicator>

      <VStack align="stretch" flex={1} gap={2}>
        {/* Title & Message */}
        <Box>
          <Alert.Title>{title}</Alert.Title>
          <Alert.Description>{errorMessage}</Alert.Description>
        </Box>

        {/* Actions */}
        {(showRetry || showDismiss || showDetails) && (
          <HStack gap={2} marginTop={1}>
            {/* Retry Button */}
            {showRetry && onRetry && (
              <Button
                size="sm"
                variant="outline"
                colorPalette={colorPalette}
                leftIcon={<LuRefreshCw />}
                onClick={onRetry}
                disabled={isRetrying}
              >
                {isRetrying ? 'Retrying...' : retryLabel}
              </Button>
            )}

            {/* Show Details Toggle */}
            {showDetails && (
              <Button
                size="sm"
                variant="ghost"
                colorPalette={colorPalette}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? 'Hide Details' : 'Show Details'}
              </Button>
            )}
          </HStack>
        )}

        {/* Error Details (Expandable) */}
        {showDetails && isExpanded && (
          <Box
            marginTop={2}
            padding={3}
            backgroundColor="rgba(0, 0, 0, 0.05)"
            borderRadius="md"
            fontSize="xs"
            fontFamily="monospace"
            overflowX="auto"
            maxHeight="200px"
            overflowY="auto"
          >
            <pre>{errorDetails}</pre>
          </Box>
        )}
      </VStack>

      {/* Dismiss Button */}
      {showDismiss && onDismiss && (
        <IconButton
          size="sm"
          variant="ghost"
          aria-label="Dismiss error"
          onClick={onDismiss}
          icon={<LuX />}
        />
      )}
    </Alert.Root>
  );
}

/**
 * ErrorAlertCompact - Compact version for inline errors
 */
export interface ErrorAlertCompactProps extends Omit<ErrorAlertProps, 'showDetails'> {
  /**
   * Hide icon
   * @default false
   */
  hideIcon?: boolean;
}

export function ErrorAlertCompact({
  error,
  message,
  hideIcon = false,
  colorPalette = 'red',
  className,
}: ErrorAlertCompactProps) {
  if (!error) return null;

  const errorMessage = message || (typeof error === 'string' ? error : error.message);

  return (
    <Box
      padding={2}
      backgroundColor={`${colorPalette}.50`}
      borderLeftWidth="3px"
      borderColor={`${colorPalette}.500`}
      borderRadius="md"
      className={className}
    >
      <HStack gap={2} alignItems="flex-start">
        {!hideIcon && <LuTriangleAlert size={16} color={`var(--colors-${colorPalette}-700)`} />}
        <Box fontSize="sm" color={`${colorPalette}.700`} flex={1}>
          {errorMessage}
        </Box>
      </HStack>
    </Box>
  );
}
